import { useState, useEffect, useContext } from 'react';
import { useTranslation, Card } from 'soxo-bootstrap-core';
import { Calendar, Skeleton, message, Select } from 'antd';
import DateSelectorIcon from '/src/assets/icons/DateSelector.svg';
import TimeSelectorIcon from '/src/assets/icons/TimeSelector.svg';
import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import { Appointments } from '../../../../../models';
import { GlobalContext } from 'soxo-bootstrap-core';
import SlotLegend, { groupSlots, SectionTitle } from '../rendering-components';
import { BlockedSlotPopover, HeldSlotPopover, UnusedSlotPopover, UsedSlotPopover } from './block-slot-popover';
import './time-selector.scss';
import { parseJSON } from '../../../../../utils/common.utils';

dayjs.extend(updateLocale);

// Show 3-letter weekdays
dayjs.updateLocale('en', {
    weekdaysShort: ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'],
});

// STS from API
export const SLOT_STATUS = {
    BOOKED: 'APT',
    BLOCKED: 'SLTBK',
    HELD: 'DESK',
    HELD_WEB: 'WEB',
    UN_USED: 'unused',
};

// map API → css classes
const slotClassMap = {
    [SLOT_STATUS.BOOKED]: 'used',
    [SLOT_STATUS.BLOCKED]: 'blocked',
    [SLOT_STATUS.HELD]: 'held',
    [SLOT_STATUS.HELD_WEB]: 'held',
    [SLOT_STATUS.UN_USED]: 'unused',
};

const slotTypes = [
    { key: 'available', label: 'Available', description: 'This slot is open for booking.' },
    { key: 'used', label: 'Booked', description: 'This slot has already been booked.' },
    { key: 'blocked', label: 'Reserved', description: 'This slot is blocked and cannot be booked.' },
    { key: 'held', label: 'On-Hold', description: 'This slot is temporarily on hold.' },
    { key: 'unused', label: 'Expired', description: 'This slot has expired and is no longer valid.' },
];

export default function TimeSelector({ onSelect, packageData, selectedSlots = [], shouldFetch, setShouldFetch }) {
    const { t } = useTranslation();
    const { settings } = useContext(GlobalContext);

    const [calendarDate, setCalendarDate] = useState(dayjs());

    const [available, setAvailable] = useState([]);

    const [selected, setSelected] = useState([]);

    const [loading, setLoading] = useState(false);

    // Group Slots
    const { morning, afternoon, evening } = groupSlots(available);

    const currentUser = parseJSON(localStorage.getItem('userInfo'));
    const otherDetails = parseJSON(currentUser?.other_details);

    const appoinmentPermission = otherDetails?.enableSlotBooking;

    // Fetch available appointment slots for a given date and package.
    const fetchAvailableSlots = async (dt, packageData, callback) => {
        if (!packageData) {
            setAvailable([]);
            return;
        }
        setLoading(true);
        try {
            const results = await Appointments.getSlots(dt.format('YYYY-MM-DD'), packageData);

            // Normalize slot data (ensure guest_details always exists)
            const slots = results.map((slot) => ({
                ...slot,
                guest_details: slot.guest_details || null,
            }));

            // If API returned a message, treat as "no slots"
            if (results.message) {
                message.info(results.message);
                setAvailable([]);
            } else {
                // Save slots and run callback
                setAvailable(slots);
                if (callback) callback(slots);
            }
        } catch {
            message.error('Failed to fetch slots');
            setAvailable([]);
        }
        setLoading(false);
    };

    // Filtered Slots
    const filteredSlots = [
        { label: 'Morning ', data: morning },
        { label: 'Afternoon ', data: afternoon },
        { label: 'Evening ', data: evening },
    ];

    /**
     * Toggles a slot selection and updates the selected state.
     **/
    const selectSlot = (slot, slotKey) => {
        setSelected((prev) => {
            // Check if slot is already selected
            const exists = prev.some((s) => s.key === slotKey);
            // Toggle selection: remove if exists, add if not
            const newSelected = exists ? prev.filter((s) => s.key !== slotKey) : [...prev, { item: slot, key: slotKey }];
            // Notify parent via callback
            onSelect(newSelected.length ? { date: calendarDate.format('YYYY-MM-DD'), slot: newSelected } : null);
            // Update state
            return newSelected;
        });
    };

    //  Effect: Fetch available slots whenever dependencies change.
    useEffect(() => {
        if (packageData) fetchAvailableSlots(calendarDate, packageData);
    }, [packageData, calendarDate, shouldFetch]);

    // Effect: Sync local `selected` state with `selectedSlots` prop.
    useEffect(() => {
        setSelected(selectedSlots);
    }, [selectedSlots]);

    return (
        <div className="time-slot-container">
            {loading ? (
                <div className="loading-text">
                    <p>{t('Loading slots')}</p>
                    <Skeleton />
                </div>
            ) : (
                <div className="calendar-container">
                    {/* Calendar */}
                    <Card className="calendar card card-shadow">
                        <SectionTitle
                            icon={DateSelectorIcon}
                            label="Select Date"
                            className="section-title-cls"
                            imgStyle={{ width: 20, height: 17, marginRight: 8 }}
                        />
                        <Calendar
                            fullscreen={false}
                            value={calendarDate}
                            onChange={(date) => {
                                if (!packageData) {
                                    message.warning(' Please select a package first');
                                    return; // stop execution
                                }
                                setCalendarDate(date);
                                // reset slots when date changes
                                setSelected([]);
                                onSelect(null);
                            }}
                            disabledDate={(current) => current && current < dayjs().startOf('day')}
                            headerRender={({ value, onChange }) => {
                                const currentYear = value.year();
                                const currentMonth = value.month();
                                const months = Array.from({ length: 12 }, (_, i) => dayjs().month(i).format('MMMM'));
                                const years = Array.from({ length: 20 }, (_, i) => dayjs().year() - 10 + i);

                                return (
                                    <div className="calendar-header-selectors">
                                        <Select
                                            value={currentYear}
                                            onChange={(newYear) => {
                                                if (!packageData) {
                                                    message.warning(' Please select a package first');
                                                    return;
                                                }
                                                onChange(value.year(newYear));
                                            }}
                                        >
                                            {years.map((year) => (
                                                <Select.Option key={year} value={year}>
                                                    {year}
                                                </Select.Option>
                                            ))}
                                        </Select>

                                        <Select
                                            value={currentMonth}
                                            onChange={(newMonth) => {
                                                if (!packageData) {
                                                    message.warning(' Please select a package first');
                                                    return;
                                                }
                                                onChange(value.month(newMonth));
                                            }}
                                        >
                                            {months.map((month, i) => (
                                                <Select.Option key={i} value={i}>
                                                    {month}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </div>
                                );
                            }}
                        />
                    </Card>

                    {/* Slots */}
                    <Card className="calendar-slots card card-shadow">
                        <div className="slots-header">
                            <SectionTitle
                                icon={TimeSelectorIcon}
                                label="Select Time"
                                className="section-title-cls"
                                // style={{ marginBottom: 15, display: 'flex', alignItems: 'center' }}
                                imgStyle={{ width: 20, height: 17, marginRight: 8 }}
                            />
                            <SlotLegend types={slotTypes} />
                        </div>

                        {filteredSlots.map(
                            ({ label, data }) =>
                                data?.length > 0 && (
                                    <div key={label}>
                                        <h4 className="slots-label">{label}</h4>
                                        <div className="time-slots">
                                            {data?.map((entry, idx) => {
                                                const slotKey = `${label}-${idx}`;
                                                const isSelected = selected.some((s) => s.key === slotKey);
                                                const slotClass =
                                                    Number(entry?.userid) === Number(currentUser?.id) &&
                                                    appoinmentPermission &&
                                                    entry.sub_mode === SLOT_STATUS.BLOCKED
                                                        ? 'blocked-available'
                                                        : slotClassMap[entry.sub_mode] || 'available';

                                                if (entry.sub_mode === SLOT_STATUS.BLOCKED) {
                                                    return Number(entry?.userid) === Number(currentUser?.id) || appoinmentPermission ? (
                                                        <div
                                                            key={slotKey}
                                                            className={`slot-item blocked-available ${isSelected ? 'active' : ''}`}
                                                            onClick={() => {
                                                                if (!packageData) {
                                                                    message.warning('Please select a package first');
                                                                    return;
                                                                }
                                                                selectSlot(entry, slotKey);
                                                            }}
                                                        >
                                                            <BlockedSlotPopover key={slotKey} entry={entry} setShouldFetch={setShouldFetch}>
                                                                <label>{entry.as_starttime}</label>
                                                            </BlockedSlotPopover>
                                                        </div>
                                                    ) : (
                                                        <BlockedSlotPopover key={slotKey} entry={entry} setShouldFetch={setShouldFetch}>
                                                            <div className="slot-item blocked">
                                                                <label>{entry.as_starttime}</label>
                                                            </div>
                                                        </BlockedSlotPopover>
                                                    );
                                                }

                                                if (entry.sub_mode === SLOT_STATUS.BOOKED) {
                                                    return (
                                                        <UsedSlotPopover key={slotKey} entry={entry}>
                                                            <div className={`slot-item used ${isSelected ? 'active' : ''}`}>
                                                                <label>{entry.as_starttime}</label>
                                                            </div>
                                                        </UsedSlotPopover>
                                                    );
                                                }

                                                if (entry.sub_mode === SLOT_STATUS.HELD || entry.sub_mode === SLOT_STATUS.HELD_WEB) {
                                                    return (
                                                        <HeldSlotPopover key={slotKey} entry={entry}>
                                                            <div className={`slot-item held ${isSelected ? 'active' : ''}`}>
                                                                <label>{entry.as_starttime}</label>
                                                            </div>
                                                        </HeldSlotPopover>
                                                    );
                                                }
                                                if (entry.sub_mode === SLOT_STATUS.UN_USED) {
                                                    return (
                                                        <UnusedSlotPopover key={slotKey} entry={entry}>
                                                            <div className={`slot-item unused ${isSelected ? 'active' : ''}`}>
                                                                <label>{entry.as_starttime}</label>
                                                            </div>
                                                        </UnusedSlotPopover>
                                                    );
                                                }

                                                return (
                                                    <div
                                                        key={slotKey}
                                                        className={`slot-item ${slotClass} ${isSelected ? 'active' : ''}`}
                                                        onClick={() => {
                                                            if (!packageData) {
                                                                message.warning(' Please select a package first');
                                                                return;
                                                            }
                                                            // allow only if slot is selectable
                                                            if (entry.sub_mode !== SLOT_STATUS.UN_USED) {
                                                                selectSlot(entry, slotKey);
                                                            }
                                                        }}
                                                    >
                                                        <label>{entry.as_starttime}</label>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )
                        )}
                    </Card>
                </div>
            )}
        </div>
    );
}
